import React, { createContext, useContext, useState } from "react";
import createPersistedState from "use-persisted-state";
import { removeLocalStorageValue } from "../Logout/helpers";
import {GuidString} from "../../models/BrandedTypes";
import { Permissions } from "../Context/memberContext/types/Permissions";

export type ImpersonationContextProperties = {
    membershipNumberOverride: string,
    accountIdOverride: GuidString,
    accountManagerPermissions?: Permissions | null,
    memberPermissions?: Permissions | null,
    impersonateMemberPermissions: boolean
}

export const localStorageKey = "persisted-membership-number-override";

export type ImpersonationContextValue = {
    membershipNumberOverride: string,
    accountIdOverride: GuidString,
    impersonationOverride: ImpersonationContextProperties | null,
    setImpersonationOverride: (impersonationProperties: ImpersonationContextProperties) => void,
    isImpersonatingMember: boolean,
    memberPermissions: Permissions | null,
    accountManagerPermissions: Permissions | null,
    impersonateMemberPermissions: boolean,
}

const MembershipImpersonationContext = createContext<ImpersonationContextValue>({
    membershipNumberOverride: null,
    accountIdOverride: null,
    impersonationOverride: null,
    setImpersonationOverride: () => {},
    isImpersonatingMember: false,
    memberPermissions: null,
    accountManagerPermissions: null,
    impersonateMemberPermissions: false,
})

export const logoutActions = [
    removeLocalStorageValue(localStorageKey)
]

export const ClearPersistedStorage = () => {
    localStorage.removeItem(localStorageKey);
}

const usePersistedMembershipNumberOverride = createPersistedState<ImpersonationContextProperties>(localStorageKey);

export const MembershipImpersonationProvider = ({children}: React.PropsWithChildren<{}>) => {
    const [impersonationOverride, setImpersonationOverride] = usePersistedMembershipNumberOverride(null);

    let accountIdOverride = impersonationOverride?.accountIdOverride ?? null;
    let membershipNumberOverride = impersonationOverride?.membershipNumberOverride ?? null;
    let memberPermissions = impersonationOverride?.memberPermissions ?? null;
    let accountManagerPermissions = impersonationOverride?.accountManagerPermissions ?? null;
    let impersonateMemberPermissions = impersonationOverride?.impersonateMemberPermissions ?? false;

    const contextValue: ImpersonationContextValue = {
        membershipNumberOverride,
        accountIdOverride,
        impersonationOverride,
        setImpersonationOverride,
        isImpersonatingMember: membershipNumberOverride !== null && accountIdOverride !== null,
        memberPermissions,
        accountManagerPermissions,
        impersonateMemberPermissions,
    };

    return (
        <MembershipImpersonationContext.Provider value={contextValue}>
            {children}
        </MembershipImpersonationContext.Provider>
    )
}

export const useMembershipImpersonationContext = () => useContext(MembershipImpersonationContext);